import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import {
  Carousel,
  CarouselProps,
  DescriptionCardProps,
  useCarouselScroll,
} from '@/components';
import { CAROUSEL_EPISODES_LIMIT } from '@/constants';
import { selectCompletedEpisodeIds } from '@/store';
import { EpisodeCard as EpisodeCardType, Referrer } from '@/types';
import { isTestModeEnabled } from '@/utils';

import {
  EpisodeCardDimensions,
  EpisodeCardGutterSpacing,
  EpisodeCardVariants,
} from '../EpisodeCard';
import { EpisodeCardWrapper } from './EpisodeCardWrapper';

export interface EpisodeCardCarouselProps {
  title?: string;
  uKey: string;
  episodes?: EpisodeCardType[];
  isLoading?: boolean;
  ViewAllLink?: CarouselProps['ViewAllLink'];
  variant?: (typeof EpisodeCardVariants)[keyof typeof EpisodeCardVariants];
  referrer: Referrer;
  descriptionCardProps?: DescriptionCardProps;
  sortCompletedEpisodesLast?: boolean;
}

export const EpisodeCardCarousel = ({
  title,
  uKey,
  episodes = [],
  isLoading,
  ViewAllLink,
  variant = EpisodeCardVariants.MEDIUM,
  referrer,
  descriptionCardProps,
  sortCompletedEpisodesLast = false,
}: EpisodeCardCarouselProps) => {
  const completedEpisodeIds = useSelector(selectCompletedEpisodeIds);

  // put verified episodes at the end
  const sortedEpisodes = useMemo(() => {
    return sortCompletedEpisodesLast
      ? [...episodes].sort((a, b) => {
          const isACompleted = completedEpisodeIds.includes(a.episodeId)
            ? 1
            : -1;
          const isBCompleted = completedEpisodeIds.includes(b.episodeId)
            ? 1
            : -1;
          return isACompleted - isBCompleted;
        })
      : episodes;
  }, [episodes, completedEpisodeIds, sortCompletedEpisodesLast]);

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const episodeList = !inView
    ? sortedEpisodes.slice(0, 1)
    : isLoading && sortedEpisodes.length === 0
      ? Array.from<undefined>({ length: CAROUSEL_EPISODES_LIMIT })
      : sortedEpisodes;

  const splitEpisodes = () => {
    const halfLength = Math.ceil(episodeList.length / 2);
    const firstRow = episodeList.slice(0, halfLength);
    const secondRow = episodeList.slice(halfLength);
    return { firstRow, secondRow };
  };

  const { firstRow, secondRow } = splitEpisodes();

  const itemsPerScroll = {
    [EpisodeCardVariants.SMALL]: 2,
    [EpisodeCardVariants.MEDIUM]: 2,
    [EpisodeCardVariants.LARGE]: 2,
    [EpisodeCardVariants.JUMBO]: 1,
  }[variant];

  const itemWidth =
    EpisodeCardDimensions[variant].width +
    EpisodeCardGutterSpacing[variant] * 8;

  const { containerId, scroll, isNearBeginning, isNearEnd } = useCarouselScroll(
    {
      itemWidth: itemsPerScroll * itemWidth,
      uKey,
      referrer,
    },
  );

  const cardProps = {
    inView,
    variant,
    referrer,
    uKey,
    isLoading,
  };

  const isTestMode = isTestModeEnabled();

  return (
    <Box ref={ref}>
      <Carousel
        title={title}
        leftScrollIcon={!isNearBeginning}
        rightScrollIcon={!isNearEnd}
        ViewAllLink={ViewAllLink}
        containerId={containerId}
        scroll={scroll}
        descriptionCardProps={descriptionCardProps}
        navIconProps={
          variant === EpisodeCardVariants.MEDIUM
            ? {
                top: 'calc(50% - 24px)',
              }
            : {}
        }>
        {variant === EpisodeCardVariants.SMALL ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}>
            <Box
              sx={{ display: 'flex', mb: EpisodeCardGutterSpacing[variant] }}>
              {firstRow.map((episode, index) => (
                <EpisodeCardWrapper
                  key={episode?.episodeId || index}
                  episode={episode}
                  index={index}
                  showTooltip={isTestMode}
                  {...cardProps}
                />
              ))}
            </Box>
            {secondRow.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  mb: EpisodeCardGutterSpacing[variant],
                }}>
                {secondRow.map((episode, index) => (
                  <EpisodeCardWrapper
                    key={episode?.episodeId || index}
                    episode={episode}
                    index={index}
                    showTooltip={isTestMode}
                    {...cardProps}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          episodeList.map((episode, index) => (
            <EpisodeCardWrapper
              key={episode?.episodeId || index}
              episode={episode}
              index={index}
              showTooltip={isTestMode}
              {...cardProps}
            />
          ))
        )}
      </Carousel>
    </Box>
  );
};
